import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authRepository, otpRepository } from '../../infrastructure/authApiRepository';
import { termsRepository } from '../../../landing/infrastructure/termsApiRepository';
import { PasswordToggleButton } from '../components/PasswordToggleButton';
import '../styles/auth.css';

const ROLES = [
    { value: 'JOB_SEEKER', icon: '👤', label: '개인회원', desc: '구직자 · 설계사' },
    { value: 'COMPANY',    icon: '🏢', label: '기업회원', desc: '보험사 · 법인' },
];

// 백엔드 OtpService.CODE_ENTRY_MINUTES(5분)와 일치 — "인증번호를 받아서 입력하는" 유효시간이고,
// 인증 확인(check) 성공 후 나머지 폼을 완료할 유예시간(VERIFIED_GRACE_MINUTES=20분)은 백엔드가
// 이메일 재인증 없이 자동으로 부여한다(check() 성공 시 서버가 만료 시각을 갱신)
const OTP_SECONDS = 300;

// 백엔드 CompanyFileService.MAX_FILE_SIZE_BYTES와 일치
const NAME_CARD_MAX_BYTES = 5 * 1024 * 1024;

const PW_CHECKS = [
    { label: '8~20자',         test: (v) => v.length >= 8 && v.length <= 20 },
    { label: '영어 대문자 포함', test: (v) => /[A-Z]/.test(v) },
    { label: '영어 소문자 포함', test: (v) => /[a-z]/.test(v) },
    { label: '숫자 포함',       test: (v) => /[0-9]/.test(v) },
];

function formatPhone(value) {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 3) return digits;
    if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
}

function loadDaumPostcode(callback) {
    if (window.daum?.Postcode) { callback(); return; }
    const script = document.createElement('script');
    script.src = '//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';
    script.onload = callback;
    document.head.appendChild(script);
}

export function RegisterPage() {
    const navigate = useNavigate();
    const submittingRef = useRef(false);

    const [role, setRole] = useState('JOB_SEEKER');
    const [form, setForm] = useState({
        email: '', password: '', passwordConfirm: '', name: '', phone: '',
        companyName: '', ceoName: '',
        companyAddress: '', companyAddressDetail: '', emailOtp: '',
    });
    const [nameCardFile, setNameCardFile] = useState(null);
    const addressDetailRef = useRef(null);
    const [errors, setErrors] = useState({});
    const [apiError, setApiError] = useState('');
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

    // OTP 상태
    const [otpRequesting, setOtpRequesting] = useState(false);
    const [otpSent, setOtpSent]             = useState(false);
    const [otpVerified, setOtpVerified]     = useState(false);
    const [otpChecking, setOtpChecking]     = useState(false);
    const [otpTimer, setOtpTimer]           = useState(OTP_SECONDS);
    const [otpExpired, setOtpExpired]       = useState(false);
    const [otpTimerKey, setOtpTimerKey]     = useState(0);
    const timerRef = useRef(null);

    function handleNameCardChange(e) {
        const file = e.target.files?.[0];
        if (!file) return;
        const extension = file.name.split('.').pop()?.toLowerCase();
        if (!['jpg', 'jpeg', 'png'].includes(extension)) {
            setErrors((prev) => ({ ...prev, nameCardFile: '허용되지 않는 파일 형식입니다. jpg, jpeg, png만 첨부해주세요.' }));
            e.target.value = '';
            return;
        }
        if (file.size > NAME_CARD_MAX_BYTES) {
            setErrors((prev) => ({ ...prev, nameCardFile: '명함 사진은 5MB 이하로 첨부해주세요.' }));
            e.target.value = '';
            return;
        }
        setErrors((prev) => ({ ...prev, nameCardFile: '' }));
        setNameCardFile(file);
        e.target.value = '';
    }

    function openPostcode() {
        loadDaumPostcode(() => {
            new window.daum.Postcode({
                oncomplete(result) {
                    const addr = result.roadAddress || result.jibunAddress;
                    setForm((f) => ({ ...f, companyAddress: addr, companyAddressDetail: '' }));
                    setTimeout(() => addressDetailRef.current?.focus(), 100);
                },
            }).open();
        });
    }

    // 약관 동의 상태
    const [serviceTerms, setServiceTerms]           = useState(null);
    const [privacyTerms, setPrivacyTerms]           = useState(null);
    const [agreedService, setAgreedService]         = useState(false);
    const [agreedPrivacy, setAgreedPrivacy]         = useState(false);
    const [showServiceModal, setShowServiceModal]   = useState(false);
    const [showPrivacyModal, setShowPrivacyModal]   = useState(false);

    useEffect(() => {
        termsRepository.getTermsByType('SERVICE').then(setServiceTerms).catch(() => {});
        termsRepository.getTermsByType('PRIVACY').then(setPrivacyTerms).catch(() => {});
    }, []);

    // 비밀번호 체크리스트 상태
    const [passwordTouched, setPasswordTouched] = useState(false);
    const [passwordBlurred, setPasswordBlurred] = useState(false);

    const set = (field) => (e) => {
        let value = e.target.value;
        if (field === 'phone') value = formatPhone(value);
        setForm((f) => {
            const next = { ...f, [field]: value };
            if (field === 'passwordConfirm' || field === 'password') {
                const pw  = field === 'password'        ? value : f.password;
                const cpw = field === 'passwordConfirm' ? value : f.passwordConfirm;
                if (cpw) {
                    setErrors((prev) => ({ ...prev, passwordConfirm: pw !== cpw ? '비밀번호가 일치하지 않습니다.' : '' }));
                }
            } else if (errors[field]) {
                setErrors((prev) => ({ ...prev, [field]: '' }));
            }
            return next;
        });
        if (field === 'email') resetOtp();
    };

    function handleBlur(field) {
        const value = form[field];
        let msg = '';
        if (field === 'email' && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
            msg = '올바른 이메일 형식이 아닙니다.';
        }
        if (field === 'passwordConfirm' && value && value !== form.password) {
            msg = '비밀번호가 일치하지 않습니다.';
        }
        if (field === 'phone') {
            const digits = value.replace(/-/g, '');
            if (!value) msg = '휴대폰 번호를 입력해주세요.';
            else if (!/^01[0-9]{8,9}$/.test(digits)) msg = '올바른 휴대폰 번호를 입력해주세요.';
        }
        if (msg) setErrors((prev) => ({ ...prev, [field]: msg }));
    }

    function resetOtp() {
        clearInterval(timerRef.current);
        setOtpSent(false);
        setOtpVerified(false);
        setOtpExpired(false);
        setOtpTimer(OTP_SECONDS);
        setForm((f) => ({ ...f, emailOtp: '' }));
    }

    useEffect(() => {
        if (!otpSent || otpVerified) return;
        setOtpTimer(OTP_SECONDS);
        setOtpExpired(false);
        timerRef.current = setInterval(() => {
            setOtpTimer((t) => {
                if (t <= 1) {
                    clearInterval(timerRef.current);
                    setOtpExpired(true);
                    return 0;
                }
                return t - 1;
            });
        }, 1000);
        return () => clearInterval(timerRef.current);
    }, [otpTimerKey]); // eslint-disable-line react-hooks/exhaustive-deps

    async function requestOtp() {
        const email = form.email.trim();
        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setErrors((e) => ({ ...e, email: '올바른 이메일을 먼저 입력해주세요.' }));
            return;
        }
        setErrors((e) => ({ ...e, email: '', emailOtp: '' }));
        setOtpRequesting(true);
        try {
            const { exists } = await authRepository.checkEmail(email);
            if (exists) {
                setErrors((e) => ({ ...e, email: '이미 가입된 이메일입니다. 다른 이메일을 입력해주세요.' }));
                return;
            }
            await otpRepository.request(email, 'EMAIL_REGISTER');
            resetOtp();
            setOtpSent(true);
            setOtpTimerKey((k) => k + 1);
        } catch (err) {
            setErrors((e) => ({ ...e, email: err.message || '인증번호 발송에 실패했습니다.' }));
        } finally {
            setOtpRequesting(false);
        }
    }

    async function verifyOtp() {
        const code = form.emailOtp.trim();
        if (code.length !== 6) {
            setErrors((e) => ({ ...e, emailOtp: '6자리 인증번호를 입력해주세요.' }));
            return;
        }
        setErrors((e) => ({ ...e, emailOtp: '' }));
        setOtpChecking(true);
        try {
            await otpRepository.check(form.email.trim(), 'EMAIL_REGISTER', code);
            clearInterval(timerRef.current);
            setOtpVerified(true);
        } catch (err) {
            setErrors((e) => ({ ...e, emailOtp: err.message || '인증번호가 올바르지 않습니다.' }));
        } finally {
            setOtpChecking(false);
        }
    }

    function validate() {
        const errs = {};
        if (!form.email) errs.email = '이메일을 입력해주세요.';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = '올바른 이메일 형식이 아닙니다.';
        if (!otpVerified) errs.emailOtp = '이메일 인증을 완료해주세요.';
        if (!form.password) errs.password = '비밀번호를 입력해주세요.';
        else if (!PW_CHECKS.every(({ test }) => test(form.password))) errs.password = 'invalid';
        if (!form.passwordConfirm) errs.passwordConfirm = '비밀번호 확인을 입력해주세요.';
        else if (form.password !== form.passwordConfirm) errs.passwordConfirm = '비밀번호가 일치하지 않습니다.';
        const phoneDigits = form.phone.replace(/-/g, '');
        if (!form.phone) errs.phone = '휴대폰 번호를 입력해주세요.';
        else if (!/^01[0-9]{8,9}$/.test(phoneDigits)) errs.phone = '올바른 휴대폰 번호를 입력해주세요.';
        if (role === 'COMPANY') {
            if (!form.companyName.trim()) errs.companyName = '기업명을 입력해주세요.';
            if (!form.ceoName.trim()) errs.ceoName = '대표자명을 입력해주세요.';
            if (!form.companyAddress.trim()) errs.companyAddress = '회사 주소를 입력해주세요.';
            if (!nameCardFile) errs.nameCardFile = '담당자 실물 명함 사진을 첨부해주세요.';
        }
        if (!agreedService) errs.agreedService = '서비스 이용약관에 동의해주세요.';
        if (!agreedPrivacy) errs.agreedPrivacy = '개인정보처리방침에 동의해주세요.';
        return errs;
    }

    async function handleSubmit(e) {
        e.preventDefault();
        if (submittingRef.current) return;
        setApiError('');
        const errs = validate();
        if (Object.keys(errs).length) {
            setErrors(errs);
            if (errs.password) { setPasswordTouched(true); setPasswordBlurred(true); }
            return;
        }
        setErrors({});
        submittingRef.current = true;
        setLoading(true);
        try {
            const payload = {
                email: form.email,
                password: form.password,
                name: form.name.trim() || undefined,
                phone: form.phone.replace(/-/g, ''),
                role,
                emailOtp: form.emailOtp,
            };
            if (role === 'COMPANY') {
                payload.companyName = form.companyName.trim();
                payload.ceoName = form.ceoName.trim();
                payload.companyAddress = form.companyAddress.trim();
                payload.companyAddressDetail = form.companyAddressDetail.trim() || undefined;
                await authRepository.registerCompany(payload, nameCardFile);
            } else {
                await authRepository.register(payload);
            }
            setDone(true);
        } catch (err) {
            setApiError(err.message);
        } finally {
            submittingRef.current = false;
            setLoading(false);
        }
    }

    function formatTimer(sec) {
        const m = Math.floor(sec / 60);
        const s = sec % 60;
        return `${m}:${String(s).padStart(2, '0')}`;
    }

    const passwordMatch   = form.passwordConfirm.length > 0 && form.password === form.passwordConfirm;
    const passwordMismatch = form.passwordConfirm.length > 0 && form.password !== form.passwordConfirm;
    const showPwChecklist = passwordTouched;
    const pwCheckFailed   = passwordBlurred || errors.password === 'invalid';

    if (done) {
        const isCompanyDone = role === 'COMPANY';
        return (
            <div className="auth-page">
                <div className="auth-card">
                    <div className="auth-success">
                        <div className="auth-success__title">
                            {isCompanyDone ? '가입 신청이 완료되었습니다' : '회원가입이 완료되었습니다'}
                        </div>
                        <p className="auth-success__desc">
                            {isCompanyDone ? (
                                <>
                                    <span className="auth-success__email">{form.email}</span>으로<br />
                                    관리자 심사 후 이용 가능합니다.
                                </>
                            ) : (
                                <>
                                    <span className="auth-success__email">{form.email}</span>으로<br />
                                    가입이 완료되었습니다.<br />
                                    지금 바로 로그인하실 수 있습니다.
                                </>
                            )}
                        </p>
                        <button className="auth-submit" onClick={() => navigate('/login')}>
                            로그인 하러 가기
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="auth-page">
            <div className="auth-card">
                <Link to="/" className="auth-card__logo">
                    <img src="/images/logo/main_logo.png" alt="RISING BROKERS" />
                </Link>
                <div className="auth-card__title">회원가입</div>
                <div className="auth-card__sub">회원 유형을 선택해주세요</div>

                <div className="auth-role-selector">
                    {ROLES.map((r) => (
                        <button
                            key={r.value}
                            type="button"
                            className={`auth-role-card${role === r.value ? ' auth-role-card--active' : ''}`}
                            onClick={() => setRole(r.value)}
                        >
                            <span className="auth-role-card__icon">{r.icon}</span>
                            <span className="auth-role-card__label">{r.label}</span>
                            <span className="auth-role-card__desc">{r.desc}</span>
                        </button>
                    ))}
                </div>

                <form className="auth-form" onSubmit={handleSubmit} noValidate>

                    {/* 계정 정보 */}
                    <div className="auth-field">
                        <label>이메일</label>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <input
                                className={`auth-input${errors.email ? ' auth-input--error' : ''}`}
                                style={{ flex: 1 }}
                                type="email" placeholder="example@email.com"
                                value={form.email} onChange={set('email')} onBlur={() => handleBlur('email')}
                                autoComplete="email"
                                disabled={otpVerified}
                            />
                            <button
                                type="button"
                                className="cp-address-btn"
                                onClick={requestOtp}
                                disabled={otpRequesting || otpVerified}
                            >
                                {otpRequesting ? '발송 중' : otpSent ? '재요청' : '인증번호 요청'}
                            </button>
                        </div>
                        {errors.email && <span className="auth-field__error">{errors.email}</span>}

                        {otpSent && !otpVerified && (
                            <div style={{ marginTop: 8 }}>
                                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                    <input
                                        className={`auth-input${errors.emailOtp ? ' auth-input--error' : ''}`}
                                        style={{ flex: 1, letterSpacing: '0.3em', textAlign: 'center' }}
                                        type="text" inputMode="numeric" maxLength={6}
                                        placeholder="인증번호 6자리"
                                        value={form.emailOtp} onChange={set('emailOtp')}
                                        disabled={otpExpired}
                                    />
                                    <span style={{ fontSize: 13, color: otpExpired ? '#e53935' : '#f16102', minWidth: 36, textAlign: 'center', fontWeight: 600 }}>
                                        {otpExpired ? '만료' : formatTimer(otpTimer)}
                                    </span>
                                    <button
                                        type="button"
                                        className="cp-address-btn"
                                        onClick={verifyOtp}
                                        disabled={otpChecking || otpExpired}
                                    >
                                        {otpChecking ? '확인 중' : '인증 확인'}
                                    </button>
                                </div>
                                {errors.emailOtp && <span className="auth-field__error">{errors.emailOtp}</span>}
                                {otpExpired && (
                                    <span className="auth-field__error">인증 시간이 만료되었습니다. 인증번호를 재요청해주세요.</span>
                                )}
                            </div>
                        )}

                        {otpVerified && (
                            <div className="auth-alert auth-alert--success" style={{ marginTop: 8, padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                                <span>✓ 이메일 인증이 완료되었습니다.</span>
                                <button
                                    type="button"
                                    onClick={resetOtp}
                                    style={{ background: 'none', border: 'none', padding: 0, fontSize: 12, fontWeight: 600, color: '#f16102', textDecoration: 'underline', cursor: 'pointer', flexShrink: 0 }}
                                >
                                    이메일 변경
                                </button>
                            </div>
                        )}

                        {!otpSent && !otpVerified && errors.emailOtp && (
                            <span className="auth-field__error">{errors.emailOtp}</span>
                        )}
                    </div>

                    <div className="auth-field">
                        <label>비밀번호</label>
                        <div className="auth-input-wrap">
                            <input
                                className={`auth-input${errors.password === 'invalid' || (errors.password && !form.password) ? ' auth-input--error' : ''}`}
                                type={showPassword ? 'text' : 'password'} placeholder="8~20자, 대소문자 + 숫자 포함"
                                value={form.password}
                                onChange={set('password')}
                                onFocus={() => setPasswordTouched(true)}
                                onBlur={() => setPasswordBlurred(true)}
                                autoComplete="new-password"
                                maxLength={20}
                            />
                            <PasswordToggleButton visible={showPassword} onToggle={() => setShowPassword((v) => !v)} />
                        </div>
                        {errors.password && errors.password !== 'invalid' && (
                            <span className="auth-field__error">{errors.password}</span>
                        )}
                        {showPwChecklist && (
                            <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                {PW_CHECKS.map(({ label, test }) => {
                                    const met = test(form.password);
                                    const color = met ? '#22c55e' : pwCheckFailed ? '#e53935' : '#9ca3af';
                                    return (
                                        <span key={label} style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 5, color }}>
                                            <span style={{ fontSize: 13, lineHeight: 1 }}>{met ? '✓' : '○'}</span>
                                            {label}
                                        </span>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    <div className="auth-field">
                        <label>비밀번호 확인</label>
                        <div className="auth-input-wrap">
                            <input
                                className={`auth-input${passwordMismatch || errors.passwordConfirm ? ' auth-input--error' : passwordMatch ? ' auth-input--valid' : ''}`}
                                type={showPasswordConfirm ? 'text' : 'password'} placeholder="비밀번호를 다시 입력"
                                value={form.passwordConfirm} onChange={set('passwordConfirm')} onBlur={() => handleBlur('passwordConfirm')}
                                autoComplete="new-password"
                            />
                            <PasswordToggleButton visible={showPasswordConfirm} onToggle={() => setShowPasswordConfirm((v) => !v)} />
                        </div>
                        {form.passwordConfirm ? (
                            <span style={{ fontSize: 12, color: passwordMatch ? '#22c55e' : '#e53935', fontWeight: 500 }}>
                                {passwordMatch ? '✓ 비밀번호가 일치합니다.' : '비밀번호가 일치하지 않습니다.'}
                            </span>
                        ) : (
                            errors.passwordConfirm && <span className="auth-field__error">{errors.passwordConfirm}</span>
                        )}
                    </div>

                    {/* 담당자 정보 */}
                    {role === 'COMPANY' && (
                        <div className="auth-divider"><span>담당자 정보</span></div>
                    )}

                    <div className="auth-field">
                        <label>
                            {role === 'COMPANY' ? '담당자명' : '이름'}
                            <span className="auth-field__optional">(선택)</span>
                        </label>
                        <input
                            className="auth-input"
                            type="text" placeholder="이름을 입력해주세요"
                            value={form.name} onChange={set('name')}
                            autoComplete="name"
                        />
                    </div>

                    <div className="auth-field">
                        <label>휴대폰 번호</label>
                        <input
                            className={`auth-input${errors.phone ? ' auth-input--error' : ''}`}
                            type="tel" placeholder="010-0000-0000"
                            value={form.phone} onChange={set('phone')} onBlur={() => handleBlur('phone')}
                            autoComplete="tel"
                        />
                        {errors.phone && <span className="auth-field__error">{errors.phone}</span>}
                    </div>

                    {/* 기업 정보 */}
                    {role === 'COMPANY' && (
                        <>
                            <div className="auth-divider"><span>기업 정보</span></div>

                            <div className="auth-field">
                                <label>기업명 <span className="cp-required">*</span></label>
                                <input
                                    className={`auth-input${errors.companyName ? ' auth-input--error' : ''}`}
                                    type="text" placeholder="기업명을 입력해주세요"
                                    value={form.companyName} onChange={set('companyName')}
                                />
                                {errors.companyName && <span className="auth-field__error">{errors.companyName}</span>}
                            </div>
                            <div className="auth-field">
                                <label>대표자명 <span className="cp-required">*</span></label>
                                <input
                                    className={`auth-input${errors.ceoName ? ' auth-input--error' : ''}`}
                                    type="text" placeholder="대표자명을 입력해주세요"
                                    value={form.ceoName} onChange={set('ceoName')}
                                />
                                {errors.ceoName && <span className="auth-field__error">{errors.ceoName}</span>}
                            </div>
                            <div className="auth-field">
                                <label>회사 주소 <span className="cp-required">*</span></label>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <input
                                        className={`auth-input${errors.companyAddress ? ' auth-input--error' : ''}`}
                                        style={{ flex: 1, cursor: 'pointer' }}
                                        type="text"
                                        placeholder="주소 검색을 눌러 입력해주세요"
                                        value={form.companyAddress}
                                        readOnly
                                        onClick={openPostcode}
                                    />
                                    <button type="button" className="cp-address-btn" onClick={openPostcode}>
                                        주소 검색
                                    </button>
                                </div>
                                {errors.companyAddress && <span className="auth-field__error">{errors.companyAddress}</span>}
                                <input
                                    className="auth-input"
                                    style={{ marginTop: 8 }}
                                    type="text"
                                    placeholder="상세 주소 (동, 호수 등)"
                                    value={form.companyAddressDetail}
                                    onChange={set('companyAddressDetail')}
                                    ref={addressDetailRef}
                                />
                            </div>

                            <div className="auth-field">
                                <label>담당자 실물 명함 사진 <span className="cp-required">*</span></label>
                                <p style={{ margin: '0 0 8px', fontSize: 12, color: '#8c8c8c' }}>
                                    회사 인증용입니다. 담당자 본인의 명함을 촬영해 올려주세요(회사명·직함·이름이 보이게).
                                    관리자만 확인하며 설계사에게는 공개되지 않습니다. 명함이 없으면 승인되지 않습니다.
                                </p>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <label
                                        className="cp-address-btn"
                                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                                    >
                                        명함 사진 선택
                                        <input
                                            type="file"
                                            accept="image/jpeg,image/png"
                                            onChange={handleNameCardChange}
                                            style={{ display: 'none' }}
                                        />
                                    </label>
                                    {nameCardFile && (
                                        <span style={{ fontSize: 13, color: '#22c55e' }}>✓ {nameCardFile.name}</span>
                                    )}
                                </div>
                                {errors.nameCardFile && <span className="auth-field__error">{errors.nameCardFile}</span>}
                            </div>
                        </>
                    )}

                    {/* 약관 동의 */}
                    <div className="auth-divider"><span>약관 동의</span></div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={agreedService}
                                onChange={(e) => setAgreedService(e.target.checked)}
                            />
                            <span style={{ fontSize: '0.9rem', color: '#333' }}>
                                <button type="button" className="reg-terms-link" onClick={() => setShowServiceModal(true)}>
                                    서비스 이용약관
                                </button>
                                에 동의합니다. <span className="cp-required">*</span>
                            </span>
                        </label>
                        {errors.agreedService && <span className="auth-field__error" style={{ marginTop: -6 }}>{errors.agreedService}</span>}

                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={agreedPrivacy}
                                onChange={(e) => setAgreedPrivacy(e.target.checked)}
                            />
                            <span style={{ fontSize: '0.9rem', color: '#333' }}>
                                <button type="button" className="reg-terms-link" onClick={() => setShowPrivacyModal(true)}>
                                    개인정보처리방침
                                </button>
                                에 동의합니다. <span className="cp-required">*</span>
                            </span>
                        </label>
                        {errors.agreedPrivacy && <span className="auth-field__error" style={{ marginTop: -6 }}>{errors.agreedPrivacy}</span>}
                    </div>

                    {apiError && <p key={apiError} className="auth-form-error">{apiError}</p>}

                    <button className="auth-submit" type="submit" disabled={loading || !otpVerified}>
                        {loading ? '처리 중...' : '회원가입'}
                    </button>
                    <p style={{ marginTop: 10, fontSize: 12, color: '#9ca3af', textAlign: 'center' }}>
                        원활한 이용을 위해 Chrome, Edge 등 최신 브라우저 사용을 권장합니다.
                    </p>
                </form>

                <div className="auth-links">
                    <span>이미 계정이 있으신가요?</span>
                    <Link to="/login">로그인</Link>
                </div>
            </div>

            {/* 서비스 이용약관 모달 */}
            {showServiceModal && (
                <div className="reg-modal-overlay" onClick={() => setShowServiceModal(false)}>
                    <div className="reg-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="reg-modal-header">
                            <h3>{serviceTerms?.title || '서비스 이용약관'}</h3>
                            <button className="reg-modal-close" onClick={() => setShowServiceModal(false)}>✕</button>
                        </div>
                        <div className="reg-modal-body">
                            {serviceTerms?.content
                                ? serviceTerms.content.split('\n').map((line, i) => <p key={i}>{line || <br />}</p>)
                                : <p>불러오는 중...</p>}
                        </div>
                        <div className="reg-modal-footer">
                            <button className="auth-submit" style={{ margin: 0 }} onClick={() => { setAgreedService(true); setShowServiceModal(false); }}>
                                동의하고 닫기
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 개인정보처리방침 모달 */}
            {showPrivacyModal && (
                <div className="reg-modal-overlay" onClick={() => setShowPrivacyModal(false)}>
                    <div className="reg-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="reg-modal-header">
                            <h3>{privacyTerms?.title || '개인정보처리방침'}</h3>
                            <button className="reg-modal-close" onClick={() => setShowPrivacyModal(false)}>✕</button>
                        </div>
                        <div className="reg-modal-body">
                            {privacyTerms?.content
                                ? privacyTerms.content.split('\n').map((line, i) => <p key={i}>{line || <br />}</p>)
                                : <p>불러오는 중...</p>}
                        </div>
                        <div className="reg-modal-footer">
                            <button className="auth-submit" style={{ margin: 0 }} onClick={() => { setAgreedPrivacy(true); setShowPrivacyModal(false); }}>
                                동의하고 닫기
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                .reg-terms-link {
                    background: none;
                    border: none;
                    padding: 0;
                    color: #f16102;
                    font-size: 0.9rem;
                    font-weight: 600;
                    cursor: pointer;
                    text-decoration: underline;
                }
                .reg-modal-overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(0,0,0,0.45);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                    padding: 16px;
                }
                .reg-modal {
                    background: #fff;
                    border-radius: 16px;
                    width: 100%;
                    max-width: 540px;
                    max-height: 80vh;
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                }
                .reg-modal-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 20px 24px 16px;
                    border-bottom: 1px solid #f0ebe6;
                }
                .reg-modal-header h3 { margin: 0; font-size: 1rem; font-weight: 700; color: #1c1814; }
                .reg-modal-close {
                    background: none;
                    border: none;
                    font-size: 1.1rem;
                    color: #888;
                    cursor: pointer;
                    padding: 4px;
                }
                .reg-modal-body {
                    flex: 1;
                    overflow-y: auto;
                    padding: 20px 24px;
                    font-size: 0.85rem;
                    color: #444;
                    line-height: 1.7;
                }
                .reg-modal-body p { margin: 0 0 4px; }
                .reg-modal-footer {
                    padding: 16px 24px;
                    border-top: 1px solid #f0ebe6;
                }
            `}</style>
        </div>
    );
}
